import { useMemo, useCallback, useState, ChangeEvent, Dispatch, SetStateAction } from 'react'

import { SortDescriptor, orderBy, process } from '@progress/kendo-data-query'
import MD5 from 'crypto-js/md5'
import { v4 as makeUuid } from 'uuid'

import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings, PredefinedFilterExceptions, OutboundPredefinedFilterExceptionType } from 'types/Settings'
import routesConfig from 'lib/routesConfig'

import { updateAccountSettings, updateDomainSettings } from 'redux/features/settings/settingsSlice'
import {
  PredefinedFilterExceptionsForm,
  PredefinedFilterExceptionsFormState,
  usePredefinedFilterExceptionsForm
} from 'components/pages/outboundSettings/contentPolicies/usePredefinedFilterExceptionsForm'

export interface State {
  isConfirmDialogOpen: boolean
  sort: SortDescriptor[]
  error: string
  form: PredefinedFilterExceptionsForm
  formState: PredefinedFilterExceptionsFormState
  tableData: {
    total: number
    data: PredefinedFilterExceptions[]
  }
}

export interface EventHandlers {
  onAdd: () => void
  onRemove: (id: string) => void
  onRemoveConfirm: () => void
  onRemoveCancel: () => void
  onBulkEdit: () => void
  handleSortChange: (e: GridSortChangeEvent) => void
  handleOnInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleOnSelectChange: (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => void
}

export type UsePredefinedFilterExceptionsTableLogic = [State, EventHandlers]

const INITIAL_FORM_STATE = { pattern: '', type: OutboundPredefinedFilterExceptionType.PHONE_NUMBER }

const BLANK_ENTRY = { ...INITIAL_FORM_STATE, id: '' }

const initialSort = [{ field: 'pattern', dir: 'asc' } as SortDescriptor]

export interface UsePredefinedFilterExceptionsTableLogicProps {
  delegateDirtyFormState: Dispatch<SetStateAction<boolean>>
}

export const usePredefinedFilterExceptionsTableLogic = ({
  delegateDirtyFormState
}: UsePredefinedFilterExceptionsTableLogicProps): UsePredefinedFilterExceptionsTableLogic => {
  const form = usePredefinedFilterExceptionsForm({
    initialState: INITIAL_FORM_STATE,
    delegateIsDirtyForm: delegateDirtyFormState
  })
  const dispatch = useAppDispatch()
  const [sort, setSort] = useState(initialSort)
  const [removeId, setRemoveId] = useState<string>('')
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false)

  const { accessTokenObject, accountSettings, domainSettings } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings
  }))

  const tableData = useMemo(() => {
    const settings = accessTokenObject?.pdDomainId ? domainSettings : accountSettings
    const parsedData = settings.outbound_predefined_message_content_filter_exceptions
      ? (JSON.parse(
          settings.outbound_predefined_message_content_filter_exceptions as string
        ) as PredefinedFilterExceptions[])
      : []
    const orderedData = orderBy(parsedData || [], sort)
    const { data } = process(orderedData, {})

    // Have the first item as an edit item.  This method works with sorting.
    data.unshift(BLANK_ENTRY)
    return {
      data,
      total: data.length || 0
    }
  }, [accessTokenObject, domainSettings, accountSettings, sort])

  const updateData = useCallback(
    (data: PredefinedFilterExceptions[]) => {
      if (accessTokenObject?.pdDomainId) {
        dispatch(
          updateDomainSettings({
            domainId: accessTokenObject.pdDomainId,
            settings: { [AvailableSettings.OUTBOUND_PREDEFINED_MESSAGE_CONTENT_FILTERS_EXCEPTIONS]: data }
          })
        )
      } else {
        dispatch(
          updateAccountSettings({
            settings: { [AvailableSettings.OUTBOUND_PREDEFINED_MESSAGE_CONTENT_FILTERS_EXCEPTIONS]: data }
          })
        )
      }
    },
    [dispatch, accessTokenObject]
  )

  const onAdd = useCallback(() => {
    const data = tableData.data.filter(entry => entry.id !== '')
    const newEntry: PredefinedFilterExceptions = {
      ...form.getState(),
      id: MD5(makeUuid()).toString()
    }
    if (form.validate(newEntry, { data })) {
      updateData([...data, newEntry])
    }
  }, [tableData.data, form, updateData])

  const onRemove = useCallback(
    (id: string) => {
      setRemoveId(id)
      setIsConfirmDialogOpen(true)
    },
    [setRemoveId, setIsConfirmDialogOpen]
  )

  const onRemoveConfirm = useCallback(() => {
    const policies = tableData.data.filter(entry => entry.id !== removeId && entry.id !== '')
    updateData(policies)
    setRemoveId('')
    setIsConfirmDialogOpen(false)
  }, [tableData.data, updateData, removeId])

  const onRemoveCancel = useCallback(() => {
    setRemoveId('')
    setIsConfirmDialogOpen(false)
  }, [setRemoveId, setIsConfirmDialogOpen])

  const onBulkEdit = useCallback(() => {
    routesConfig.OUTBOUND_SETTINGS_PREDEFINED_FILTERS_EXCEPTIONS_BULK_EDIT.goto()
  }, [])

  const handleOnInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      form.fields.pattern.setValue(e.target.value)
      form.delegateIsDirty()
    },
    [form]
  )

  const handleOnSelectChange = useCallback(
    (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      if (typeof e.target.value === 'string') {
        form.fields.type.setValue(e.target.value)
        form.delegateIsDirty()
      }
    },
    [form]
  )

  const handleSortChange = useCallback((e: GridSortChangeEvent) => {
    setSort(e.sort)
  }, [])

  return useMemo(
    () => [
      {
        isConfirmDialogOpen,
        sort,
        error: form.error,
        form,
        formState: form.getState(),
        tableData
      },
      {
        onAdd,
        onRemove,
        onRemoveConfirm,
        onRemoveCancel,
        onBulkEdit,
        handleSortChange,
        handleOnInputChange,
        handleOnSelectChange
      }
    ],
    [
      isConfirmDialogOpen,
      sort,
      form,
      tableData,
      onAdd,
      onRemove,
      onRemoveConfirm,
      onRemoveCancel,
      onBulkEdit,
      handleSortChange,
      handleOnInputChange,
      handleOnSelectChange
    ]
  )
}
